<template>
  <scrollable-container :class="[$style.scrollableContainer]">
    <placeholder
      v-show="isTableAwaiting"
      :text="text"
      @buttonClick="buildReport"
    />
    <loader v-show="isTableLoading" />
    <container
      id="printable"
      :class="[{ [$style.message]: isTableAwaiting }, $style.scrollableContent]"
    >
      <template v-if="isSummaryUserReport && isTableReady">
        <div :class="$style.userHead">
          <h2>
            {{ $t('reports.summaryUserReport') }}
            <strong :class="$style.strong">{{ reportTree[0].nameUser }}</strong>
          </h2>
          <h2>
            {{ $t('global.from') }} {{ formatDate(dateRange.dateFrom) }} {{ $t('global.to') }}
            {{ formatDate(dateRange.dateTo) }}
          </h2>
        </div>
        <h2 :class="$style.title">
          {{ $t('global.status') }}
        </h2>
        <table :class="$style.table">
          <tbody>
            <tr
              v-for="column in columnsStatus"
              :key="column.name"
              :class="$style.item"
            >
              <td :class="$style.user">
                {{ column.name }}
              </td>
              <td
                :class="[$style.dangerous, dangerousClass(reportTree[0].statusDangerous)]"
                :style="{ backgroundColor: getColor(reportTree[0].reportData[column.field], column.field) }"
              >
                {{ getDataField(reportTree[0], column.field) }}
              </td>
            </tr>
          </tbody>
        </table>

        <table-body
          :key-report="'activityProductivity'"
          :data="activityProductivityData"
          :columns="columnsActivityProductivity"
          :title="$t('reports.activityProductivityIndicators')"
          clickable
          @goTo="scrollTo"
        />

        <table-body
          :data="userPrograms"
          :columns="columnsProgram"
          :title="$t('reports.mostFrequentlyUsedPrograms')"
          :is-count-row="true"
        />

        <table-body
          :data="visitedWebsites"
          :columns="columnsWebsites"
          :title="$t('reports.mostFrequentlyVisitedWebsites')"
          :is-count-row="true"
        />
      </template>
      <table
        v-else-if="isTableReady"
        :class="$style.table"
      >
        <thead>
          <tr :class="$style.header">
            <td
              v-for="column in displayedColumns"
              :key="column.name"
              @click="changeSort(column)"
            >
              <div :class="$style.tdInline">
                {{ column.name }}
              </div>
              <img
                src="@/assets/images/icons/table/sort-arrow.svg"
                :class="[
                  $style.tdInline,
                  $style.arrow,
                  {
                    [$style.asc]: column.sort == 2,
                    [$style.hidden]: column.sort == 0,
                  },
                ]"
              >
            </td>
          </tr>
        </thead>
        <tr
          v-if="!isSummaryReport"
          :class="$style.total"
        >
          <td>{{ $t('global.total') }}</td>
          <td>{{ convertSecondsToDays(getTotal[0]) }}</td>
          <td>{{ convertSecondsToDays(getTotal[1]) }}</td>
          <td>{{ convertSecondsToDays(getTotal[2]) }}</td>
          <td>{{ getTotal[3].toFixed(2) }}</td>
          <td>{{ convertSecondsToDays(getTotal[4]) }}</td>
          <td>{{ convertSecondsToDays(getTotal[5]) }}</td>
          <td>{{ getTotal[6] }}</td>
          <td>{{ convertSecondsToDays(getTotal[7]) }}</td>
          <td>{{ convertSecondsToDays(getTotal[8]) }}</td>
          <td v-if="isConsolidatedFull">
            {{ convertSecondsToDays(getTotal[9]) }}
          </td>
          <td v-if="isConsolidatedFull">
            {{ convertSecondsToDays(getTotal[10]) }}
          </td>
          <td v-if="isConsolidatedFull">
            {{ convertSecondsToDays(getTotal[11]) }}
          </td>
          <td v-if="isConsolidatedFull">
            {{ getTotal[12] }}
          </td>
        </tr>

        <tbody
          v-for="(department, index) in sortedDeps"
          :key="index"
        >
          <tr
            v-show="department.isVisible"
            :class="[
              $style.item,
              { [$style.itemNoCursor]: isSummaryReport },
              {
                [$style.red]:
                  !isSummaryReport &&
                  !isFolder(department) &&
                  (department.reportData.overwork < 0 || department.reportData.outOfWork > 0),
              },
            ]"
            @contextmenu.prevent="showMenu($event, department)"
          >
            <td
              :style="indent(department.depth)"
              :class="[
                { [$style.department]: isFolder(department) },
                { [$style.user]: !isFolder(department) },
                { [$style.expandCell]: isSummaryReport && isFolder(department) },
              ]"
              @click="expand(department)"
            >
              <div
                v-if="isFolder(department)"
                :class="[$style.expandButton, { [$style.expanded]: department.isExpanded }]"
              />
              <img
                v-if="isFolder(department)"
                :class="$style.iconFolder"
                src="@/assets/images/icons/computer/folder.svg"
              >

              <div v-if="isFolder(department)">
                {{ department.reportData.name }}
              </div>

              <div v-if="!isFolder(department)">
                {{ department.nameUser }}
              </div>
            </td>
            <td
              v-for="column in displayedColumns.slice(1)"
              :key="column.name"
              :class="[
                $style.dangerous,
                { [dangerousClass(department.statusDangerous)]: column.field === 'securityStatus' },
                {
                  [$style.scrollCell]:
                    isSummaryReport &&
                    !isFolder(department) &&
                    (column.field === 'triggers' || column.field === 'securityStatus'),
                },
              ]"
              :style="{ backgroundColor: getColor(department.reportData[column.field], column.field) }"
              @click="scrollToTriggers(department.id, column.field)"
            >
              {{ getDataField(department, column.field) }}
            </td>
          </tr>
        </tbody>
      </table>
      <template v-if="(isSummaryReport || isSummaryUserReport) && isTableReady">
        <draggable
          v-if="isSummaryReport"
          :class="$style.draggableBoard"
        >
          <advanced-list
            v-for="({ data, reportId }, index) in barChartData"
            :key="index"
            :data="getReport(reportId)"
            :class="[$style.advList, $style.pdfContent]"
          >
            <bar-chart
              :id="index"
              :chart="data"
              :data="getReport(reportId)"
            />
          </advanced-list>
        </draggable>

        <table-work
          ref="summaryLateEarly"
          :data="compArray"
          :columns="columnsLate"
          :table-name="'summaryLateEarly'"
          :title="$t('reports.summaryLateEarly')"
          :class="$style.pdfContent"
        />

        <table-work
          ref="summaryAbsent"
          :data="compArray"
          :columns="columnsOut"
          :table-name="'summaryAbsent'"
          :title="$t('reports.summaryAbsent')"
          :class="$style.pdfContent"
        />

        <table-work
          ref="triggersTable"
          :data="triggersData"
          :columns="columnsTriggers"
          :table-name="'triggers'"
          :title="$t('reports.summarySecurity')"
          :class="$style.pdfContent"
          clickable
        />

        <table-files
          v-for="(data, index) in fileData"
          :key="index"
          :data="data"
          :index="index"
          :class="$style.pdfContent"
        />
      </template>
      <div
        v-if="!isSummaryReport && !isSummaryUserReport && isTableReady"
        :class="$style.notScrollableContent"
      >
        <tabset
          :tabs="tabs"
          :opened="opened"
          :hidden-text="hiddenText"
          :tab-style="tabStyle"
          @tabClick="openNewTab"
          @toggle="toggle"
        />
        <barchart
          v-show="tabs[0].active && opened"
          :bars="overworkMax"
          :rect-style="rectStyleOverworkMax"
          :text-style="textStyleOverworkMax"
          :is-convert-needed="true"
        />
        <barchart
          v-show="tabs[1].active && opened"
          :bars="overworkMin"
          :rect-style="rectStyleOverworkMin"
          :text-style="textStyleOverworkMin"
          :is-convert-needed="true"
        />
        <barchart
          v-show="tabs[2].active && opened"
          :bars="outWorkMax"
          :rect-style="rectStyleOutOfWork"
          :text-style="textStyleOutOfWork"
          :is-convert-needed="false"
        />
      </div>
    </container>

    <context-menu
      v-show="isMenuVisible"
      id="contextmenu"
      :user-id="selectedUserId"
      :mouse-x="mouseX"
      :mouse-y="mouseY"
      :date-from="dateRange.dateFrom"
      :date-to="dateRange.dateTo"
      :is-worktime-only="isWorktimeOnly"
      :pages="pages"
    />
  </scrollable-container>
</template>

<script>
  import ScrollableContainer from '@/components/common/blocks/scrollable-container.vue';
  import Container from '@/components/common/blocks/container.vue';
  import Placeholder from '@/components/common/blocks/report-placeholder.vue';
  import Tabset from '@/components/common/tabs/single-view-tabs.vue';
  import Barchart from '@/components/common/bars/top-bar-chart.vue';
  import { getSumTable, getFileUpload, getWorktimeTable } from '@/api/methods/reports/users/index';
  import { convertSecondsToDays, dangerousTitle, formatDate } from '@/helpers/format-date';
  import ContextMenu from '@/components/common/contextmenu/custom-contextmenu.vue';
  import Loader from '@/components/common/blocks/loader.vue';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
  import { ROUTE_NAMES } from '@/components/pages/const/const';
  import { HEAT_MAP_COLORS } from '@/views/reports/const/const';
  import draggable from 'vuedraggable';
  import AdvancedList from '@/views/computers/advanced/advancedList.vue';
  import BarChart from '@/views/computers/dashboard/bar-chart.vue';
  import { CHILDREN_DASHBORD, REPORT_IDS_SUMMARY } from '@/views/computers/dashboard/const/const';
  import { getWebsiteStat, getProgramStat } from '@/api/methods/reports/statistics/index';
  import TableFiles from '@/views/reports/users/files/table-files.vue';
  import { getDataField } from '../worktime/table-field';
  import {
    COLUMNS_CONSOLIDATED,
    COLUMNS_SUMMARY,
    COLUMNS_LATE,
    COLUMNS_OUT,
    COLUMNS_TRIGGERS,
    COLUMNS_TRIGGERS_USER,
    COLUMNS_STATUS,
    COLUMNS_ACTIVITY_PRODUCTIVITY,
    COLUMNS_PROGRAM,
    COLUMNS_SECURETY,
    COLUMNS_RISK,
    COLUMNS_WEBSITES,
    CLICKABLE_KEYS_MAP,
  } from './const/const';
  import TableWork from './table-worktime.vue';
  import TableBody from './table-body.vue';

  export default {
    components: {
      ScrollableContainer,
      Container,
      Barchart,
      Tabset,
      Placeholder,
      ContextMenu,
      Loader,
      draggable,
      AdvancedList,
      BarChart,
      TableFiles,
      TableWork,
      TableBody,
    },
    data() {
      return {
        exportArray: [],
        opened: true,
        reportTree: [],
        fileData: [],
        compArray: [],
        selectedUserId: null,
        mouseX: null,
        mouseY: null,
        isMenuVisible: false,
        pages: [
          {
            name: 'worktime',
            text: this.$t('navigation.reports.userWork.worktime.name'),
          },
          {
            name: 'productivity',
            text: this.$t('navigation.reports.userWork.productivity.name'),
          },
          {
            name: 'summaryUserReport',
            text: this.$t('navigation.reports.userWork.summaryUserReport.name'),
          },
        ],
        numberOfTopComputers: 5,
        tableArray: [],
        columns: COLUMNS_CONSOLIDATED,
        columnsLate: COLUMNS_LATE,
        columnsOut: COLUMNS_OUT,
        columnsTriggers: COLUMNS_TRIGGERS,
        columnsStatus: COLUMNS_STATUS,
        columnsActivityProductivity: COLUMNS_ACTIVITY_PRODUCTIVITY,
        columnsProgram: COLUMNS_PROGRAM,
        columnsWebsites: COLUMNS_WEBSITES,
        columnsSecurety: COLUMNS_SECURETY,
        columnsRisk: COLUMNS_RISK,
        text: this.$t('reports.pressButtonToGenerateReport'),
        total: {
          work: 0,
          active: 0,
          idle: 0,
          percent: 0,
          plan: 0,
          overwork: 0,
          out: 0,
          holiday: 0,
          meetings: 0,
          productive: 0,
          notProductive: 0,
          neutral: 0,
          printer: 0,
        },
        overworkMax: [],
        overworkMin: [],
        outWorkMax: [],
        tabStyle: {
          padding: '14px 20px 17px 20px',
          fontSize: '16px',
        },
        rectStyleOverworkMin: {
          height: '17px',
          fill: '#ffb0b0',
        },
        rectStyleOverworkMax: {
          height: '17px',
          fill: '#7CD890',
        },
        rectStyleOutOfWork: {
          height: '17px',
          fill: '#94C2E0',
        },
        textStyleOverworkMin: {
          fill: '$red',
          fontSize: '12px',
          letterSpacing: '-0.24px',
        },
        textStyleOverworkMax: {
          fill: '$green',
          fontSize: '12px',
          letterSpacing: '-0.24px',
        },
        textStyleOutOfWork: {
          fill: '#1970A7',
          fontSize: '12px',
          letterSpacing: '-0.24px',
        },
        hiddenText: this.$t('reports.top5Show'),
        tabs: [
          {
            title: this.$t('reports.tabs.topOverwork'),
            active: true,
          },
          {
            title: this.$t('reports.tabs.topUnderwork'),
            active: false,
          },
          {
            title: this.$t('reports.tabs.topAbsences'),
            active: false,
          },
        ],
        webTree: [],
        productive: {
          productiveWeb: [],
          unProductiveWeb: [],
          productivePC: [],
          unProductivePC: [],
        },
        activityProductivityData: {},
        userPrograms: [],
        visitedWebsites: [],
      };
    },
    computed: {
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      isTableAwaiting() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.REQUEST_AWAITING;
      },
      isTableLoading() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
      },
      name() {
        return this.$t(`navigation.reports.userWork.${this.$route.name}.name`);
      },
      isConsolidated() {
        return this.$route.name === ROUTE_NAMES.CONSOLIDATED;
      },
      isConsolidatedFull() {
        return this.$route.name === ROUTE_NAMES.CONSOLIDATED_FULL;
      },
      isSummaryReport() {
        return this.$route.name === ROUTE_NAMES.SUMMARY_REPORT;
      },
      isSummaryUserReport() {
        return this.$route.name === ROUTE_NAMES.SUMMARY_USER_REPORT;
      },
      getTotal() {
        return Object.values(this.total);
      },
      dateRange() {
        return this.$store.getters['filters/formattedDateRange'];
      },
      checkedComputers() {
        return this.$store.getters['pageSpecificData/checkedComputersList'];
      },
      selectedComputer() {
        return this.$store.getters['filters/selectedComputer'];
      },
      computersTree() {
        if (this.isSummaryUserReport) return [this.selectedComputer];

        const tree = this.$store.getters['pageSpecificData/computersTree'];
        let treeCopy = JSON.parse(JSON.stringify(tree));

        treeCopy = this.cutUncheckedNodes(treeCopy);
        return treeCopy;
      },
      computers() {
        return this.getSelectedComputers(this.computersTree);
      },

      displayedColumns() {
        if (this.isConsolidated) return this.columns.slice(0, 10);
        return this.columns;
      },
      sortedColumn() {
        const index = this.columns.findIndex((x) => x.sort > 0);
        const s = this.columns[index].sort;

        return { index, sort: s };
      },
      sortedDeps() {
        const calc = this.reportTree.slice(0).sort(this.compare);

        calc.forEach((element) => {
          if (element.type === 'folder') this.sortNodes(element.children);
        });

        this.updateTableArray(calc);

        if (this.exportArray.length === 0 && this.tableArray.length > 0) this.makeExportArray();
        return this.tableArray;
      },
      isWorktimeOnly() {
        return this.$store.getters['filters/isWorktimeOnly'];
      },

      barChartData() {
        return REPORT_IDS_SUMMARY.map((reportId) => ({ data: this.getList(reportId), reportId }));
      },
    },
    watch: {
      $route() {
        this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
      },
      dateRange(oldValue, newValue) {
        if (
          oldValue.dateFrom.toString() !== newValue.dateFrom.toString() ||
          oldValue.dateTo.toString() !== newValue.dateTo.toString()
        ) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      checkedComputers(oldArray, newArray) {
        if (oldArray !== newArray) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      selectedComputer(oldVal, newVal) {
        if (oldVal !== newVal) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      isWorktimeOnly(oldValue, newValue) {
        if (oldValue !== newValue) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
    },
    async created() {
      if (this.isSummaryReport) this.columns = COLUMNS_SUMMARY;
      this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
    },
    methods: {
      formatDate(date) {
        return formatDate(date);
      },
      scrollTo(key) {
        const refName = CLICKABLE_KEYS_MAP[key];

        if (!refName) return;

        const table = this.$refs[refName];
        if (table && typeof table.scrollToRow === 'function') {
          table.scrollToRow(this.reportTree[0].id);
        }
      },
      scrollToTriggers(id, field) {
        if (!this.isSummaryReport || (field !== 'triggers' && field !== 'securityStatus')) return;
        const { triggersTable } = this.$refs;
        if (triggersTable && typeof triggersTable.scrollToRow === 'function') {
          triggersTable.scrollToRow(id);
        }
      },
      getComputers(data) {
        if (data.type === 'folder') {
          data.children.forEach((child) => this.getComputers(child));
        } else {
          this.compArray.push(data);
        }
      },

      getSelectedComputers(tree) {
        return tree.flatMap((item) =>
          item?.children ? this.getSelectedComputers(item.children) : [{ ...item, checked: true }],
        );
      },

      getReport(reportId) {
        return CHILDREN_DASHBORD.find(({ id }) => id === reportId);
      },
      getValue(value) {
        return (value / 3600).toFixed(1);
      },
      getList(reportId) {
        const { field } = this.getReport(reportId);

        if (this.productive[field]) {
          return this.productive[field].map(({ computer, time }) => ({
            name: computer,
            value: this.getValue(time),
          }));
        }
        if (field === 'web') {
          return this.webTree.map(({ userName, activeTime }) => ({
            name: userName,
            value: this.getValue(activeTime),
          }));
        }
        return this.compArray.map(({ nameUser, reportData }) => ({
          name: nameUser,
          value: field === 'printer' ? reportData[field] : this.getValue(reportData[field]),
        }));
      },
      getColor(value, field) {
        if (field !== 'productivityStatus') return null;
        return HEAT_MAP_COLORS[value];
      },
      dangerousClass(statusDangerous) {
        switch (statusDangerous) {
          case 0:
            return this.$style.green;
          case 1:
            return this.$style.yellow;
          case 2:
            return this.$style.red;
          default:
            return null;
        }
      },
      getDataField(data, field) {
        if (field === 'securityStatus') return dangerousTitle(data.statusDangerous);
        return getDataField(data.reportData[field], field);
      },
      setExportData(exportData) {
        if (this.isConsolidated) {
          this.$store.commit('exportData/setConsolidated', exportData);
        }
        if (this.isConsolidatedFull) {
          this.$store.commit('exportData/setConsolidatedFull', exportData);
        }

        if (this.isSummaryReport) {
          this.$store.commit('exportData/setSummaryReport', this.computersTree);
        }

        if (this.isSummaryReport) {
          this.$store.commit('exportData/setSummaryReport', exportData);
        }
      },
      makeExportArray() {
        this.tableArray.forEach((item) => {
          const resObj = {};
          if (item.type !== 'folder') {
            for (let j = 0; j < this.displayedColumns.length; j++) {
              const { field } = this.displayedColumns[j];
              const res = item.reportData[field];
              if (field === 'name' || field === 'outOfWork') resObj[this.displayedColumns[j].name] = res;
              else if (field === 'activePercent') resObj[this.displayedColumns[j].name] = res.toFixed(2);
              else resObj[this.displayedColumns[j].name] = convertSecondsToDays(res);
            }
            this.exportArray.push(resObj);
          }
        });

        const exportData = { name: this.name, data: this.exportArray };

        this.setExportData(exportData);
      },
      updateTableArray(calc) {
        this.tableArray = [];
        calc.forEach((node) => this.generateArrayFromTree(node, 1));
      },
      changeVisibility(item, bool) {
        item.isVisible = bool;
        if (item.type === 'folder' && item.isExpanded === bool && item.children) {
          item.children.forEach((child) => this.changeVisibility(child, bool));
        }
      },
      expand(item) {
        if (item.type === 'folder') {
          item.isExpanded = !item.isExpanded;

          if (item.children) {
            item.children.forEach((child) => this.changeVisibility(child, item.isExpanded));
          }
        }
      },
      isFolder(node) {
        if (node.type === 'folder') return true;
        return false;
      },
      sortNodes(nodes) {
        nodes.sort(this.compare);

        nodes.forEach((node) => {
          if (node.type === 'folder') this.sortNodes(node.children);
        });
      },
      convertSecondsToDays(secNum) {
        return convertSecondsToDays(secNum);
      },
      cutUncheckedNodes(tree) {
        const childrenList = [];

        if (tree) {
          tree.forEach((node) => {
            if (node.checked) childrenList.push(node);
            else if (node.type === 'folder' && node.children) {
              node.children = this.cutUncheckedNodes(node.children);

              if (node.children.length > 0) childrenList.push(node);
            }
          });
        }

        return childrenList;
      },
      putInTopList(value, topComputers, name) {
        for (let i = this.numberOfTopComputers - 1; i >= 0; i--) {
          if (Math.abs(value) >= topComputers[i].time && (i === 0 || Math.abs(value) <= topComputers[i - 1].time)) {
            topComputers.splice(i, 0, {
              computer: name,
              time: Math.abs(value),
            });

            if (topComputers.length > this.numberOfTopComputers) topComputers.splice(this.numberOfTopComputers);

            break;
          }
        }
      },
      generateTopLists(node) {
        if (node.type === 'computer') {
          if (node.reportData.overwork > 0) {
            this.putInTopList(node.reportData.overwork, this.overworkMax, node.reportData.name);
          } else if (node.reportData.overwork < 0) {
            this.putInTopList(node.reportData.overwork, this.overworkMin, node.reportData.name);
          }

          this.putInTopList(node.reportData.outOfWork, this.outWorkMax, node.reportData.name);
        } else {
          node.children.forEach((child) => this.generateTopLists(child));
        }
      },
      generateArrayFromTree(node, depth) {
        node.depth = depth;

        this.tableArray.push(node);

        if (node.type === 'folder') {
          node.children.forEach((child) => this.generateArrayFromTree(child, depth + 1));
        }
      },
      checkNullBars(topBarsList) {
        topBarsList.forEach((bar) => {
          if (bar.computer === null) topBarsList.splice(topBarsList.indexOf(bar));
        });
      },
      fillWithNull(arrayName) {
        arrayName = Array(this.numberOfTopComputers).fill({
          computer: null,
          time: 0,
        });
        return arrayName;
      },
      async buildReport() {
        try {
          if (this.dateRange.dateFrom && this.dateRange.dateTo && this.computersTree) {
            this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);
            await this.fetch();
            return this.reportTree;
          }

          return this.reportTree;
        } catch (error) {
          console.error(error);
          return this.reportTree;
        } finally {
          if (this.reportTree?.length) {
            this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);
          } else {
            this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_NO_DATA);
          }
        }
      },
      async fetch() {
        this.reportTree = [];
        this.exportArray = [];
        this.compArray = [];
        let workTimeOnly = 0;
        const route =
          this.isConsolidatedFull || this.isSummaryReport || this.isSummaryUserReport
            ? ROUTE_NAMES.CONSOLIDATED_FULL
            : ROUTE_NAMES.CONSOLIDATED;

        if (this.isWorktimeOnly) workTimeOnly = 1;

        const { dateFrom, dateTo } = this.dateRange;

        const { data: sumTableData } = await getSumTable(this.computersTree, dateFrom, dateTo, workTimeOnly, route);

        if (sumTableData.success) {
          this.reportTree = sumTableData.result.computersTree;
          if (!this.isSummaryReport && !this.isSummaryUserReport) {
            Object.keys(this.total).forEach((key) => {
              this.total[key] = 0;
            });

            this.reportTree.forEach((node) => {
              this.total.work += node.reportData.workTime;
              this.total.active += node.reportData.activeTime;
              this.total.idle += node.reportData.idleTime;
              this.total.percent += parseFloat((node.reportData.activePercent / this.reportTree.length).toFixed(2));
              this.total.plan += node.reportData.workPlan;
              this.total.overwork += node.reportData.overwork;
              this.total.out += node.reportData.outOfWork;
              this.total.holiday += node.reportData.holidayWork;
              this.total.meetings += node.reportData.meetings;
              if (this.isConsolidatedFull || this.isSummaryReport) {
                this.total.productive += node.reportData.productive;
                this.total.notProductive += node.reportData.notProductive;
                this.total.neutral += node.reportData.neutral;
                this.total.printer += node.reportData.printer;
              }
            });

            this.tableArray = [];

            this.overworkMax = this.fillWithNull(this.overworkMax);
            this.overworkMin = this.fillWithNull(this.overworkMin);
            this.outWorkMax = this.fillWithNull(this.outWorkMax);

            this.reportTree.forEach((node) => this.generateTopLists(node));

            this.checkNullBars(this.overworkMax);
            this.checkNullBars(this.overworkMin);
            this.checkNullBars(this.outWorkMax);
          }
        }

        let progStat;
        let websiteStat;
        let fileUpload;

        if (this.isSummaryReport || this.isSummaryUserReport) {
          const [programResponse, websiteResponse, fileUploadResponse] = await Promise.all([
            getProgramStat(this.computers, dateFrom, dateTo),
            getWebsiteStat(this.computers, dateFrom, dateTo),
            getFileUpload(this.computers, dateFrom, dateTo, this.$i18n.locale),
          ]);

          progStat = programResponse.data;
          websiteStat = websiteResponse.data;
          fileUpload = fileUploadResponse.data;

          this.productive.productiveWeb = websiteStat?.result.productivePC;
          this.productive.unProductiveWeb = websiteStat?.result.unProductivePC;
          this.webTree = websiteStat?.result.users;

          this.productive.productivePC = progStat?.result.productivePC;
          this.productive.unProductivePC = progStat?.result.unProductivePC;

          this.fileData = fileUpload?.result;

          this.reportTree.forEach((item) => this.getComputers(item));
          this.triggersData = this.compArray;
        }

        if (this.isSummaryUserReport) {
          const { data: worktimeData } = await getWorktimeTable(this.computersTree, dateFrom, dateTo, workTimeOnly);
          const { workTime, meetings, outOfWork, late, productive, notProductive } = this.reportTree[0].reportData;

          const workTimeDates = worktimeData.result[0]?.dates;

          const workingDayCountByFlag = workTimeDates?.reduce(
            (count, { isWorkingDay }) => count + (isWorkingDay ? 1 : 0),
            0,
          );

          const workingDayCount =
            workingDayCountByFlag || workTimeDates?.reduce((count, item) => count + (item.workTime ? 1 : 0), 0);

          const averageWorkdayDuration = workingDayCount
            ? convertSecondsToDays(Math.round(workTime / workingDayCount))
            : 0;

          const activeTimeValue = workTimeDates?.reduce((count, { activeTime }) => count + activeTime, 0);
          const idleTimeValue = workTimeDates?.reduce((count, { idleTime }) => count + idleTime, 0);

          this.activityProductivityData = {
            totalWorkedHours: convertSecondsToDays(workTime),
            averageWorkdayDuration,
            activeIdleTime: `${convertSecondsToDays(activeTimeValue)} / ${convertSecondsToDays(idleTimeValue)}`,
            awayFromPc: convertSecondsToDays(meetings),
            absencesCount: outOfWork,
            lateCount: late,
            productiveHours: convertSecondsToDays(productive),
            unproductiveHours: convertSecondsToDays(notProductive),
          };

          const userHistory = Object.values(progStat?.result?.users?.[0]?.history || {});

          this.userPrograms = userHistory.flatMap(({ categoryName, events = [] }, id) =>
            events.map((event) => ({
              id,
              categoryName,
              ...event,
              name: event.name === 'No description' ? event.link.split('\\').pop() : event.name,
            })),
          );

          const websitesHistory = Object.values(websiteStat?.result?.users?.[0]?.history || {});

          this.visitedWebsites = websitesHistory.flatMap(({ categoryName, events = [] }, id) =>
            events.map((event) => ({
              id,
              categoryName,
              ...event,
            })),
          );
          this.columnsTriggers = COLUMNS_TRIGGERS_USER;
          const { reasonIds } = this.reportTree[0].reportData;
          const isSecurityReason = (id) => id >= 82 && id <= 87;

          const securIds = reasonIds.filter(isSecurityReason);
          const prodIds = reasonIds.filter((id) => !isSecurityReason(id));

          const { name } = this.reportTree[0];
          this.triggersData = [
            {
              name,
              reportData: {
                category: this.$t('reports.columns.security'),
                triggers: securIds?.length,
                reasonIds: securIds,
              },
            },
            {
              name,
              reportData: {
                category: this.$t('reports.columns.productivity'),
                triggers: prodIds?.length,
                reasonIds: prodIds,
              },
            },
          ];
          this.$store.commit('exportData/setSummaryUserReport', this.computersTree);
          this.$store.commit('exportData/setSummaryUserReport', { name: this.name, data: this.computersTree });
        }
      },
      openNewTab(index) {
        this.tabs.forEach((item) => {
          item.active = false;
        });

        this.tabs[index].active = true;
      },
      toggle() {
        this.opened = !this.opened;
      },
      showMenu(event, user) {
        if (!this.isFolder(user)) {
          this.selectedUserId = user.id;
          this.mouseX = event.clientX;
          this.mouseY = event.clientY;
          this.isMenuVisible = true;

          window.addEventListener('click', (e) => {
            if (!document.getElementById('contextmenu').contains(e.target)) {
              this.isMenuVisible = false;
              window.removeEventListener('click', e);
            }
          });
        }
      },
      indent(depth) {
        return {
          paddingLeft: `${depth * 10}px`,
        };
      },
      compare(a, b) {
        const { index } = this.sortedColumn;
        const key = this.columns[index].field;

        a = a.reportData[key];
        b = b.reportData[key];

        if (index === 0 && a && b) {
          a = a.toLowerCase();
          b = b.toLowerCase();
        }

        if (this.sortedColumn.sort === 1) {
          if (a > b) {
            return -1;
          }

          if (a < b) {
            return 1;
          }
          return 0;
        }

        if (this.sortedColumn.sort === 2) {
          if (a < b) {
            return -1;
          }

          if (a > b) {
            return 1;
          }

          return 0;
        }
        return 0;
      },
      changeSort(column) {
        const { sort } = column;

        this.columns.forEach((col) => {
          col.sort = 0;
        });

        if (sort === 1) {
          column.sort = 2;
        } else column.sort = 1;
      },
    },
  };
</script>

<style lang="scss" module>
  .hidden {
    display: none;
  }

  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .scrollableContent {
    margin: 0 8px 0 0;
    padding: 0 12px 0 20px;
    box-shadow: $shadow;
    flex: 1;
  }

  .message {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .iconFolder {
    padding-right: 5px;
  }

  .notScrollableContent {
    margin: 20px 8px 0 0;
    padding: 0 12px 20px 20px;
    overflow: unset;
  }

  .table {
    width: 100%;
    margin-bottom: 15px;
    border-collapse: collapse;
    font-weight: 400;
    word-break: break-word;
    border: 1px solid $borderColor;
    padding-bottom: 20px;
  }

  .header {
    background: #dbf1ff;
    height: 31px;
    position: sticky;
    top: -2px;
    z-index: 3;

    td {
      border: 1px solid $borderColor;
      align-items: center;
      text-align: center;
      color: #232326;
      letter-spacing: -0.08px;
      font-size: 13px;
      padding: 7px 4px 4px 10px;
      cursor: pointer;
    }
  }

  .tdInline {
    display: inline-flex;
  }

  .arrow {
    float: right;
    margin-top: 7px;
    margin-right: 5px;

    &.asc {
      transform: rotate(180deg);
    }

    &.hidden {
      visibility: hidden;
    }
  }

  .total {
    height: 55px;
    cursor: pointer;

    &:hover {
      background: $light-gray;
    }

    td {
      border: 1px solid $borderColor;
      align-items: center;
      text-align: center;
      font-weight: 300;
      font-size: 14px;
      line-height: 14px;
      color: $black;
      padding: 19px 4px 0px 5px;
    }
  }

  .item {
    height: 29px;
    cursor: pointer;
    border-bottom: 0.2px solid #eee;

    &:hover {
      background: $light-gray;
    }

    &.red {
      background-color: #ffb0b0;

      &:hover {
        background-color: $red;
      }
    }

    td {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      letter-spacing: -0.24px;
      color: $black;
      font-weight: 300;
      font-size: 12px;
      line-height: 14px;
      text-align: center;
      padding: 0px 5px;

      &.user {
        font-size: 14px;
        letter-spacing: 0px;
        text-align: left;
        padding-top: 7px;
        padding-right: 4px;
        padding-bottom: 7px;
      }

      &.department {
        font-weight: 500;
        font-size: 14px;
        font-weight: 400;
        letter-spacing: 0px;
        text-align: left;
        padding: 7px 4px 7px 1px;
        display: flex;
        position: relative;
        border-right: 0px;
      }
    }
  }

  .itemNoCursor {
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  .scrollCell {
    cursor: pointer;
    background-color: transparent;
    transition: background-color 0.15s ease-out, font-size 0.15s ease-out, font-weight 0.15s ease-out;
    font-size: 12px;
    font-weight: 300;

    &:hover {
      background-color: $light-gray;
      font-size: 13px;
      font-weight: 400;
    }
  }

  .expandCell {
    cursor: pointer;
    transition: background-color 0.12s ease-out;

    &:hover {
      background: $light-gray;
    }
  }

  .expandButton {
    flex-shrink: 0;
    margin-right: 13px;
    width: 14px;
    height: 14px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 1px solid $branchColor;
    background: $white;
    z-index: 1;
    margin-left: -17px;

    &::before,
    &::after {
      content: '';
      background-color: $blue;
      width: 8px;
      height: 2px;
      position: absolute;
      transition: 0.25s ease-out;
    }

    &::before {
      transform: rotate(90deg);
    }

    &::after {
      transform: rotate(180deg);
    }

    &.expanded {
      &::before {
        transform: rotate(0);
      }

      &::after {
        transform: rotate(0);
      }
    }
  }

  .dangerous {
    &.green {
      background-color: $green;
    }

    &.yellow {
      background-color: $yellow;
    }

    &.red {
      background-color: $red;
    }
  }

  .draggableBoard {
    display: grid;
    grid-template-columns: repeat(3, auto);
    column-gap: 71px;
    row-gap: 23px;
    padding: 30px 40px;
    break-inside: avoid;
    page-break-inside: avoid;

    @media (max-width: 1450px) {
      grid-template-columns: repeat(2, auto);
    }
  }

  .advList {
    height: fit-content;
    max-height: 200px;
    max-width: 400px;
  }

  .title {
    padding: 10px;
  }

  .userHead {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
  }

  .strong {
    font-weight: bold;
  }

  .pdfContent {
    break-inside: avoid;
    page-break-inside: avoid;
  }
</style>
